import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toIsoDay } from '@/lib/datetime';
import { cn } from '@/lib/utils';
import { useWorkItems } from '../queries';
import { workItemRef } from '../paths';
import type { Project, WorkItem } from '../types';
import { useSelectedWorkItem } from '../useSelectedWorkItem';
import { filterParams, type WorkItemFilters } from './workItemFilters';
import { WorkItemDetailDrawer } from './WorkItemDetailDrawer';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Work items placed on a month grid by `due_date`. */
export function WorkItemsCalendar({ project, filters }: { project: Project; filters?: WorkItemFilters }) {
  const itemsQuery = useWorkItems({ project: project.id, page_size: 500, ...filterParams(filters) });
  const [selected, setSelected] = useSelectedWorkItem();
  // `month` = first day of the displayed month. Seeded once from today.
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const byDay = useMemo(() => {
    const map = new Map<string, WorkItem[]>();
    for (const item of itemsQuery.data?.results ?? []) {
      if (!item.due_date) continue;
      const bucket = map.get(item.due_date) ?? [];
      bucket.push(item);
      map.set(item.due_date, bucket);
    }
    return map;
  }, [itemsQuery.data]);

  const cells = useMemo(() => {
    const start = new Date(month);
    start.setDate(1 - month.getDay()); // back up to the week's Sunday
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [month]);

  if (itemsQuery.isPending) return <Skeleton className="h-96 w-full" />;

  const monthLabel = month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <>
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-sm font-medium">{monthLabel}</h3>
        <div className="ml-auto flex gap-1">
          <Button
            variant="outline" size="icon" className="size-7"
            aria-label="Previous month"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="outline" size="icon" className="size-7"
            aria-label="Next month"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 overflow-hidden rounded-md border text-sm">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-muted-foreground border-b bg-muted/40 px-2 py-1.5 text-xs font-medium">
            {w}
          </div>
        ))}
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === month.getMonth();
          const dayItems = byDay.get(toIsoDay(d)) ?? [];
          return (
            <div
              key={i}
              className={cn(
                'min-h-24 border-b border-r p-1 [&:nth-child(7n+7)]:border-r-0',
                inMonth ? '' : 'bg-muted/20 text-muted-foreground',
              )}
            >
              <div className="px-1 text-xs">{d.getDate()}</div>
              <div className="mt-1 space-y-1">
                {dayItems.slice(0, 3).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelected(workItemRef(item))}
                    className="bg-primary/10 hover:bg-primary/20 block w-full truncate rounded px-1 py-0.5 text-left text-[11px]"
                    title={item.title}
                  >
                    {item.title}
                  </button>
                ))}
                {dayItems.length > 3 ? (
                  <span className="text-muted-foreground px-1 text-[10px]">+{dayItems.length - 3} more</span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <WorkItemDetailDrawer project={project} workItemId={selected} onClose={() => setSelected(null)} />
    </>
  );
}
